import {
  useCuentaBancariaTesoreriaById,
  useCuentasBancariasAll,
} from "api/CuentaBancaria.hook"
import { useTarjetaBancaria } from "api/TarjetaBancaria.hook"
import { createConnection } from "common/utils/createSignalR"
import notification from "common/utils/notification"
import { useAfterbanksNotifications } from "context/afterbankContext"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

interface afterBanksRespProps {
  id: string
  message: string
  error: string
}

export const useRealTimeAfterbanks = () => {
  const { t } = useTranslation()
  const { tarjetaDetailInvalidation } = useTarjetaBancaria({
    bankDetailsFilterId: "",
  })
  const { invalidateCuentaBancariaById } = useCuentaBancariaTesoreriaById()
  const { invalidateCuentaBancariaAll } = useCuentasBancariasAll({})

  const { notifications, notificationFinishedProcess } =
    useAfterbanksNotifications()

  const notificationsRef = useRef(notifications)

  useEffect(() => {
    notificationsRef.current = notifications
  }, [notifications])

  const connectionAfterbanksHub = createConnection({
    hub: import.meta.env.REACT_APP_API_AFTERBANKS_SOCKET,
    baseURL: import.meta.env.REACT_APP_GATEWAY,
  })

  const afterBankConnection = async () => {
    if (!sessionStorage.getItem("access_token")) return
    try {
      await connectionAfterbanksHub.start()

      /**
       * SyncBankAccountById
       */
      connectionAfterbanksHub.on(
        "SyncBankAccountByIdStarted",
        (resp: afterBanksRespProps) => {
          notification.success({
            body: `${t("notification.afterbanksCardSyncStarted", {
              cardNumber: resp.message,
            })}`,
          })
        }
      )
      connectionAfterbanksHub.on(
        "SyncBankAccountByIdSuccessful",
        (resp: afterBanksRespProps) => {
          notification.success({
            body: `${t("notification.afterbanksCardSyncSuccess", {
              cardNumber: resp.message,
            })}`,
          })
          const found = notificationsRef.current.find(
            (notification) => notification.idExterno === resp.id
          )
          tarjetaDetailInvalidation({ id: found?.id })
          notificationFinishedProcess(resp.id)
        }
      )
      connectionAfterbanksHub.on("SyncBankAccountByIdError", (resp) => {
        notification.errorServidor({
          body: `${t("notification.afterbanksSyncError")}`,
        })
        notificationFinishedProcess(resp.id)
      })

      /**
       * RevokeBankAccountById
       */
      connectionAfterbanksHub.on(
        "RevokeBankAccountByIdSuccess",
        (resp: afterBanksRespProps) => {
          notification.success({
            body: `${t("notification.afterbanksAccountRevokeAuthSuccess", {
              account: resp.message,
            })}`,
          })
          invalidateCuentaBancariaAll()
        }
      )

      connectionAfterbanksHub.on(
        "RevokeBankAccountByIdError",
        (resp: afterBanksRespProps) => {
          notification.errorServidor({
            body: resp.message,
          })
        }
      )

      /**
       * SyncUpBankAccountPSD2ById
       */
      connectionAfterbanksHub.on(
        "SyncBankAccountPSD2ByIdStarted",
        (resp: afterBanksRespProps) => {
          notification.success({
            body: `${t("notification.afterbanksAccountSyncStarted", {
              account: resp.message,
            })}`,
          })
        }
      )
      connectionAfterbanksHub.on(
        "SyncBankAccountPSD2ByIdSuccessful",
        (resp: afterBanksRespProps) => {
          notification.success({
            body: `${t("notification.afterbanksAccountSyncSuccess", {
              account: resp.message,
            })}`,
          })
          const found = notificationsRef.current.find(
            (notification) => notification.idExterno === resp.id
          )
          invalidateCuentaBancariaAll()
          invalidateCuentaBancariaById({ id: found?.id })
          notificationFinishedProcess(resp.id)
        }
      )
      connectionAfterbanksHub.on("SyncBankAccountPSD2ByIdError", (resp) => {
        notification.errorServidor({
          body: `${t("notification.afterbanksSyncError")}`,
        })
        notificationFinishedProcess(resp.id)
      })

      /**
       * InsertBankAccountByConsent
       */
      connectionAfterbanksHub?.on(
        "InsertBankAccountByConsentSuccessful",
        (resp: string) => {
          notification.success({ body: resp })
        }
      )

      connectionAfterbanksHub?.on(
        "InsertBankAccountByConsentError",
        (error: string) => {
          notification.errorServidor({
            body: error,
          })
        }
      )
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error conectando a AfterbanksHub:", error)
    }
  }

  useEffect(() => {
    afterBankConnection()
    return () => {
      connectionAfterbanksHub?.stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
